import QuizInteractiveFlow from "@/components/storyboard/quiz/QuizInteractiveFlow";
import QuizMatching, {
  type QuizMatchingState,
} from "@/components/storyboard/quiz/QuizMatching";
import {
  buildMatchingEntryReview,
  parseMatchingAssignments,
} from "@/components/storyboard/quiz/review/buildEntryReview";
import { useQuizValidations } from "@/hooks/useQuizValidations";
import { InteractiveElementStatusKind } from "@/lib/interactiveElementState";
import type { QuizMatchingEntryData } from "@/types/storyboard";
import type { InteractiveIllustrationKind } from "@/lib/interactiveIllustration";

export interface QuizMatchingFlowProps {
  id: string;
  title: string;
  entries: QuizMatchingEntryData[];
  illustrationKind?: InteractiveIllustrationKind;
  onValidate: (
    pairs: { firstPart: string; secondPart: string }[],
    currentIndex: number
  ) => Promise<boolean> | boolean;
}

/**
 * Matching (Zuordnung) quiz in the shared interactive-element shell — inline
 * in the storyboard card. Used for MATCHING_DEFINITION and MATCHING_SENTENCE.
 * The verdict comes from the server, so a finished or half-finished quiz is
 * restored from `validation_data` on any device.
 */
const QuizMatchingFlow = ({
  id,
  title,
  entries,
  illustrationKind = "matching_definition",
  onValidate,
}: QuizMatchingFlowProps) => {
  const {
    status: statusOverride,
    isReady: validationsReady,
    refresh,
  } = useQuizValidations(
    id,
    entries.length,
    (answeredCount, correctCount): QuizMatchingState => ({
      currentIndex: answeredCount,
      isSubmitted: false,
      isCorrect: null,
      assignments: Object.fromEntries(
        (entries[answeredCount]?.pairs ?? []).map((pair) => [
          pair.secondPart,
          null,
        ])
      ),
      activeTerm: null,
      correctCount,
    }),
    (validation) =>
      buildMatchingEntryReview(
        entries[validation.entryIndex],
        parseMatchingAssignments(validation.userInput),
        !!validation.result.correct
      )
  );
  const serverResume =
    statusOverride?.kind === InteractiveElementStatusKind.InProgress
      ? (statusOverride.partial as QuizMatchingState)
      : undefined;

  return (
    <QuizInteractiveFlow<QuizMatchingState>
      id={id}
      title={title}
      illustrationKind={illustrationKind}
      layout="inline"
      statusOverride={statusOverride}
      reportProgress
      renderQuiz={({
        initialPartial,
        initialIndex,
        onPartialChange,
        onComplete,
      }) => {
        const seedReady = initialPartial != null || validationsReady;
        if (!seedReady) return null;

        return (
          <QuizMatching
            entries={entries}
            onValidate={async (...args) => {
              const verdict = await onValidate(...args);
              refresh();
              return verdict;
            }}
            initialPartial={initialPartial ?? serverResume}
            initialIndex={initialIndex ?? serverResume?.currentIndex}
            onPartialChange={onPartialChange}
            onComplete={onComplete}
          />
        );
      }}
    />
  );
};

export default QuizMatchingFlow;
